import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, LogOut, Paperclip, MessageSquare, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const SECTION_LABEL = { quantitative: "القدرات الكمية", verbal: "القدرات اللفظية" };

export default function SectionTopics() {
    const { section } = useParams();
    const nav = useNavigate();
    const { user, logout } = useAuth();
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCat, setActiveCat] = useState("");

    const load = () => {
        const params = { section };
        if (activeCat) params.category_id = activeCat;
        api.get("/topics", { params }).then((r) => setTopics(r.data)).catch((e) => toast.error(formatApiError(e)));
    };

    useEffect(() => {
        api.get("/categories", { params: { section } }).then((r) => setCategories(r.data)).catch(() => {});
    }, [section]);

    useEffect(load, [section, activeCat]);

    const rootPath = user?.role === "student" ? "/student" : user?.role === "parent" ? "/parent" : "/";

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => nav(rootPath)} className="text-white/80 hover:text-white hover:bg-white/10"><ArrowRight className="w-4 h-4 ml-1" /> الرئيسية</Button>
                        <BrandLogo size={36} />
                    </div>
                    <div className="flex items-center gap-2">
                        <NotificationBell onMessages={() => nav("/messages")} />
                        <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10"><LogOut className="w-4 h-4" /></Button>
                    </div>
                </div>
            </header>

            <main className="max-w-5xl mx-auto p-6 space-y-5">
                <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">القسم</div>
                    <h1 className={`text-3xl font-bold ${section === "quantitative" ? "text-[#1B2A4E]" : "text-[#C8102E]"}`}>{SECTION_LABEL[section]}</h1>
                    <p className="text-sm text-slate-500 mt-1">{topics.length} موضوع</p>
                </div>

                {categories.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        <button onClick={() => setActiveCat("")} data-testid="cat-all" className={`px-3 py-1.5 rounded-full text-xs border transition-all ${!activeCat ? "bg-[#1B2A4E] text-white border-[#1B2A4E]" : "bg-white text-slate-600 border-slate-200 hover:border-[#1B2A4E]"}`}>الكل</button>
                        {categories.map((c) => (
                            <button key={c.id} onClick={() => setActiveCat(c.id)} data-testid={`cat-${c.id}`} className={`px-3 py-1.5 rounded-full text-xs border transition-all ${activeCat === c.id ? "bg-[#1B2A4E] text-white border-[#1B2A4E]" : "bg-white text-slate-600 border-slate-200 hover:border-[#1B2A4E]"}`}>{c.name}</button>
                        ))}
                    </div>
                )}

                <div className="space-y-3">
                    {topics.length === 0 && <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-slate-200">لا توجد موضوعات في هذا القسم بعد</div>}
                    {topics.map((t) => (
                        <button
                            key={t.id}
                            onClick={() => nav(user?.role === "trainer" ? `/trainer/topic/${t.id}` : `/student/topic/${t.id}`)}
                            data-testid={`topic-${t.id}`}
                            className="w-full text-right bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md hover:-translate-y-0.5 transition-all"
                        >
                            <div className="flex items-start gap-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex flex-wrap items-center gap-2 mb-1">
                                        <h3 className="font-semibold text-[#0F172A] text-lg">{t.title}</h3>
                                        {t.is_new_for_me && <Badge className="bg-[#C8102E] text-white hover:bg-[#C8102E]">جديد</Badge>}
                                        {t.has_attachments && <Badge variant="outline" className="text-slate-500"><Paperclip className="w-3 h-3 ml-0.5" /></Badge>}
                                        {t.quiz?.questions?.length > 0 && <Badge variant="outline" className="text-[#C8102E] border-[#FECACA]"><ClipboardCheck className="w-3 h-3 ml-0.5" /> اختبار</Badge>}
                                    </div>
                                    <div className="text-xs text-slate-500 font-mono-num" dir="ltr">
                                        {t.trainer_name} • {new Date(t.created_at).toLocaleString("ar-EG")} • <MessageSquare className="inline w-3 h-3" /> {t.comments_count || 0}
                                    </div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            </main>
        </div>
    );
}
